import { lazyLoadView } from '@router/helpers'
import store from '@state/store'

export default [
  {
    path: '/company/:companyId/events',
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "events-company" */ './views/events-company.vue'
        )
      ),
    meta: {
      authRequired: true,
      // HACK: In order to share data between the `beforeResolve` hook
      // and the `props` function, we must create an object for temporary
      // data only used during route resolution.
      tmp: {},
      beforeResolve({ meta, params: { companyId } }, routeFrom, next) {
        meta.tmp.companyId = parseInt(companyId)
        next()
      },
    },
    // Set the events from the route params, once it's set in the
    // beforeResolve route guard.
    props: ({ meta }) => ({
      companyId: meta.tmp.companyId,
      events: meta.tmp.events,
    }),
    children: [
      // some extra calls to getByToken are made here and we shouldn't
      buildEventsTabRoute('', 'companyEvents'),
      buildEventsTabRoute('my-events', 'myEvents'),
      buildEventsTabRoute('past', 'pastEvents'),
    ],
  },
  {
    path: '/company/:companyId/events/new',
    name: 'createNewEvent',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "new-event" */ './views/new-event.vue')
      ),
    meta: {
      authRequired: true,
      tmp: {},
      beforeResolve({ meta, params: { companyId } }, routeFrom, next) {
        meta.tmp.companyId = parseInt(companyId)
        handleRoute()
        function handleRoute() {
          store
            .dispatch('events/fetchGroupsHierarchy', { companyId })
            .then(() => next())
            .catch(() => next({ name: '404', params: { resource: 'Event' } }))
        }
      },
    },
    props: (route) => ({
      companyId: route.meta.tmp.companyId,
    }),
  },
  {
    path: '/company/:companyId/events/:eventId/edit',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "edit-event" */ './views/edit-event.vue')
      ),
    meta: {
      authRequired: true,
      tmp: {},
      beforeResolve({ meta, params: { eventId, companyId } }, routeFrom, next) {
        meta.tmp.companyId = parseInt(companyId)
        meta.tmp.eventId = parseInt(eventId)
        handleRoute()
        function handleRoute() {
          return Promise.all([
            store.dispatch('events/fetchEventDetails', { eventId }),
            store.dispatch('events/fetchGroupsHierarchy', { companyId }),
          ])
            .then(() => {
              const { events } = store.state.events
              const event =
                (events && events.find(({ id }) => id === parseInt(eventId))) ||
                undefined
              if (!event) {
                store
                  .dispatch('events/fetchEvent', {
                    eventId,
                  })
                  .then(() => next())
              } else {
                next()
              }
            })
            .catch(() => next({ name: '404', params: { resource: 'Event' } }))
        }
      },
    },
    // Set the event from the route params, once it's set in the
    // beforeResolve route guard.
    props: ({ meta }) => ({
      companyId: meta.tmp.companyId,
      eventId: meta.tmp.eventId,
    }),
    children: [
      {
        path: '',
        name: 'editEventDetails',
        component: () =>
          lazyLoadView(
            import(
              /* webpackChunkName: "event-details" */ './views/subviews/event-details.vue'
            )
          ),
        meta: {
          authRequired: true,
          tmp: {},
        },
        props: ({ meta }) => ({
          tab: 'details',
          companyId: meta.tmp.companyId,
          eventId: meta.tmp.eventId,
        }),
      },
      {
        path: 'settings',
        name: 'editEventSettings',
        component: () =>
          lazyLoadView(
            import(
              /* webpackChunkName: "event-settings" */ './views/subviews/event-settings.vue'
            )
          ),
        meta: {
          authRequired: true,
          tmp: {},
        },
        props: ({ meta }) => ({
          tab: 'settings',
          companyId: meta.tmp.companyId,
          eventId: meta.tmp.eventId,
        }),
      },
      {
        path: 'checkin',
        name: 'editEventCheckin',
        component: () =>
          lazyLoadView(
            import(
              /* webpackChunkName: "event-checkin" */ './views/subviews/event-checkin.vue'
            )
          ),
        meta: {
          authRequired: true,
          tmp: {},
        },
        props: ({ meta }) => ({
          tab: 'checkin',
          companyId: meta.tmp.companyId,
          eventId: meta.tmp.eventId,
        }),
      },
    ],
  },
  {
    path: '/company/:companyId/events/:eventId',
    name: 'eventDetails',
    component: () =>
      lazyLoadView(
        import(/* webpackChunkName: "new-event" */ './views/event-company.vue')
      ),
    meta: {
      authRequired: true,
      tmp: {},
      parentName: 'company',
      beforeResolve({ meta, params: { companyId, eventId } }, routeFrom, next) {
        meta.tmp.companyId = parseInt(companyId)
        meta.tmp.eventId = parseInt(eventId)
        meta.tmp.events = []
        handleRoute()
        function handleRoute() {
          const event = store.getters['events/eventById'](eventId)
          if (!event) {
            store
              .dispatch('events/fetchEvent', {
                eventId,
              })
              .then((event) => {
                meta.tmp.event = event
                next()
              })
          } else {
            meta.tmp.event = event
            next()
          }
        }
      },
    },
    props: ({ meta }) => ({
      companyId: meta.tmp.companyId,
      event: meta.tmp.event,
    }),
  },
]

// // loads and registers a local store if not already loaded
// function loadStoreModule(moduleName, store) {
//   return new Promise((resolve) => {
//     if (!store.state[moduleName]) {
//       import(/* webpackChunkName: "users-store" */ './store.js').then((e) => {
//         store.registerModule(moduleName, e.default)
//         resolve()
//       })
//     } else {
//       resolve()
//     }
//   })
// }

function buildEventsTabRoute(path, name) {
  return {
    path: path,
    name: name,
    component: () =>
      lazyLoadView(
        import(
          /* webpackChunkName: "events-company-tab" */ './views/subviews/events-company-tab.vue'
        )
      ),
    meta: {
      authRequired: true,
      tmp: {},
    },
    props: (route) => ({
      tab: path,
      companyId: route.meta.tmp.companyId,
    }),
  }
}
